/* MOCK DATA — all functions below return static seed data.
   Replace with real /v2 API calls when backend is connected. */

import type {
  VcmClient,
  VcmCase,
  VcmGoal,
  VcmHouseholdMember,
  VcmInteraction,
  FollowUp,
  VcmReferral,
  VcmResource,
  VcmDocument,
  CaseMetrics,
  EventMetrics,
  OrgWorkload,
  SearchFilters,
  PaginatedResult,
  AddressRecord,
  VcmAuditEntry,
  VcmWorkOrder,
  CallLogEntry,
} from '../types/vcm';
import {
  seedClients,
  seedCases,
  seedGoals,
  seedHouseholdMembers,
  seedInteractions,
  seedFollowUps,
  seedReferrals,
  seedResources,
  seedDocuments,
  seedAddressRecords,
  seedCallLog,
} from './vcm-seed-data';

export function mockGetClients(): VcmClient[] {
  return [...seedClients];
}

export function mockGetClientById(id: string): VcmClient | null {
  return seedClients.find((c) => c.id === id) ?? null;
}

export function mockSearchClients(
  query: string,
  type: 'client' | 'case' | 'address',
  filters: SearchFilters,
): PaginatedResult<VcmClient> {
  const q = query.toLowerCase().trim();
  let results = [...seedClients];
  if (type === 'client' && q) {
    results = results.filter((c) =>
      `${c.firstName} ${c.lastName} ${c.id} ${c.phoneHome} ${c.email}`.toLowerCase().includes(q),
    );
  }
  if (filters.county && filters.county !== 'All counties') {
    results = results.filter((c) => c.county === filters.county);
  }
  if (filters.program) {
    results = results.filter((c) => c.program === filters.program);
  }
  return { data: results, total: results.length };
}

export function mockGetCases(): VcmCase[] {
  return [...seedCases];
}

export function mockGetCaseById(caseId: string): VcmCase | null {
  return seedCases.find((c) => c.caseId === caseId) ?? null;
}

export function mockGetCasesByClientId(clientId: string): VcmCase[] {
  return seedCases.filter((c) => c.clientId === clientId);
}

export function mockGetCaseMetrics(): CaseMetrics {
  return {
    openCases: 48,
    followUpsDue: 12,
    recoveryGoals: 76,
    openReferrals: 29,
    goalsInProgress: 48,
    followUpsDueToday: 4,
    referralsAwaitingOutcome: 8,
  };
}

export function mockGetEventMetrics(): EventMetrics {
  return {
    eventClients: 342,
    openDcmCases: 214,
    openReferrals: 139,
    goalsCompleted: 418,
    clientsLast30Days: 18,
    dcmCasePercentage: 62,
    referralsAwaitingOutcome: 31,
    goalCompletionRate: 74,
  };
}

export function mockGetOrgWorkloads(): OrgWorkload[] {
  return [
    {
      organization: 'Texas Veterans Commission',
      openCases: 84,
      tier34Cases: 46,
      followUpsDue: 19,
      openReferrals: 48,
    },
    {
      organization: 'Travis County Veterans Services',
      openCases: 71,
      tier34Cases: 38,
      followUpsDue: 15,
      openReferrals: 51,
    },
    {
      organization: 'Guadalupe County VSO',
      openCases: 59,
      tier34Cases: 24,
      followUpsDue: 12,
      openReferrals: 40,
    },
  ];
}

export function mockGetGoalsByCaseId(caseId: string): VcmGoal[] {
  return seedGoals.filter((g) => g.caseId === caseId);
}

export function mockGetHouseholdByClientId(clientId: string): VcmHouseholdMember[] {
  return seedHouseholdMembers.filter((m) => m.clientId === clientId);
}

export function mockGetInteractionsByCaseId(caseId: string): VcmInteraction[] {
  return seedInteractions.filter((i) => i.caseId === caseId);
}

export function mockGetFollowUp(caseId: string): FollowUp | null {
  return seedFollowUps.find((f) => f.caseId === caseId) ?? null;
}

export function mockGetReferralsByCaseId(caseId: string): VcmReferral[] {
  return seedReferrals.filter((r) => r.caseId === caseId);
}

/** Add a referral to the in-memory seed data (session-only). */
export function mockAddReferral(referral: VcmReferral): void {
  seedReferrals.push(referral);
}

/** All follow-ups across cases (used by My Cases cockpit + dashboards). */
export function mockGetAllFollowUps(): FollowUp[] {
  return [...seedFollowUps];
}

/** All referrals across cases (used by My Cases cockpit + admin). */
export function mockGetAllReferrals(): VcmReferral[] {
  return [...seedReferrals];
}

/** All service goals across cases (used by cockpit summaries). */
export function mockGetAllGoals(): VcmGoal[] {
  return [...seedGoals];
}

export function mockGetResources(): VcmResource[] {
  return [...seedResources];
}

export function mockGetDocumentsByCaseId(caseId: string): VcmDocument[] {
  return seedDocuments.filter((d) => d.caseId === caseId);
}

export function mockGetAddressRecords(): AddressRecord[] {
  return [...seedAddressRecords];
}

/* --- Dashboard-specific mock data --- */

export interface StatusBreakdown {
  label: string;
  count: number;
  color: 'success' | 'warning' | 'info' | 'default';
}

export interface TierRow {
  tier: string;
  count: number;
  total: number;
  description: string;
  accent?: boolean;
  color: 'error' | 'warning' | 'info' | 'primary';
}

export interface ActivityItem {
  id: string;
  initials: string;
  primary: string;
  secondary: string;
  timestamp: string;
  caseId: string;
}

export interface FollowUpItem {
  id: string;
  initials: string;
  clientName: string;
  caseId: string;
  type: string;
  dueLabel: string;
  overdue?: boolean;
}

export interface AttentionItem {
  id: string;
  primary: string;
  reason: string;
  severity: 'error' | 'warning' | 'info' | 'success';
}

export function mockGetStatusBreakdown(): StatusBreakdown[] {
  return [
    { label: 'Open', count: 24, color: 'success' },
    { label: 'Review', count: 11, color: 'warning' },
    { label: 'Pending', count: 8, color: 'info' },
    { label: 'Closed', count: 5, color: 'default' },
  ];
}

export function mockGetTierDistribution(): TierRow[] {
  return [
    {
      tier: 'Tier 4',
      count: 9,
      total: 48,
      description: 'Weekly contact',
      accent: true,
      color: 'error',
    },
    { tier: 'Tier 3', count: 13, total: 48, description: 'Bi-weekly contact', color: 'warning' },
    { tier: 'Tier 2', count: 14, total: 48, description: 'Monthly contact', color: 'info' },
    { tier: 'Tier 1', count: 12, total: 48, description: 'Monthly monitoring', color: 'primary' },
  ];
}

export function mockGetRecentActivity(): ActivityItem[] {
  return [
    {
      id: 'act-1',
      initials: 'EM',
      primary: 'Elena Martinez',
      secondary: 'Service goal updated',
      timestamp: '28m',
      caseId: 'CASE-4879-10482',
    },
    {
      id: 'act-2',
      initials: 'DC',
      primary: 'David Chen',
      secondary: 'Partner case submitted',
      timestamp: '1h',
      caseId: 'CASE-4879-10541',
    },
    {
      id: 'act-3',
      initials: 'PW',
      primary: 'Patricia Williams',
      secondary: 'Disability referral created',
      timestamp: '2h',
      caseId: 'CASE-4879-10555',
    },
    {
      id: 'act-4',
      initials: 'JN',
      primary: 'James Nguyen',
      secondary: 'Follow-up completed',
      timestamp: '3h',
      caseId: 'CASE-4879-10496',
    },
    {
      id: 'act-5',
      initials: 'RJ',
      primary: 'Robert Jackson',
      secondary: 'Transportation referral added',
      timestamp: '5h',
      caseId: 'CASE-4879-10528',
    },
  ];
}

export function mockGetUpcomingFollowUps(): FollowUpItem[] {
  return [
    {
      id: 'fu-d1',
      initials: 'EM',
      clientName: 'Elena Martinez',
      caseId: 'CASE-4879-10482',
      type: 'Phone',
      dueLabel: 'Overdue',
      overdue: true,
    },
    {
      id: 'fu-d2',
      initials: 'DC',
      clientName: 'David Chen',
      caseId: 'CASE-4879-10541',
      type: 'Phone',
      dueLabel: 'Today',
    },
    {
      id: 'fu-d3',
      initials: 'PW',
      clientName: 'Patricia Williams',
      caseId: 'CASE-4879-10555',
      type: 'In-person',
      dueLabel: 'Mon, Jun 9',
    },
    {
      id: 'fu-d4',
      initials: 'AT',
      clientName: 'Aisha Thompson',
      caseId: 'CASE-4879-10517',
      type: 'Phone',
      dueLabel: 'Wed, Jun 11',
    },
    {
      id: 'fu-d5',
      initials: 'JN',
      clientName: 'James Nguyen',
      caseId: 'CASE-4879-10496',
      type: 'Phone',
      dueLabel: 'Fri, Jun 20',
    },
  ];
}

export function mockGetNeedsAttention(): AttentionItem[] {
  return [
    {
      id: 'att-1',
      primary: '4 overdue follow-ups',
      reason: 'Veterans not contacted past scheduled date',
      severity: 'error',
    },
    {
      id: 'att-2',
      primary: '3 expiring housing plans',
      reason: 'Vouchers expiring within 30 days',
      severity: 'warning',
    },
    {
      id: 'att-3',
      primary: '5 referrals awaiting outcome',
      reason: 'Referrals made but not yet resolved',
      severity: 'info',
    },
    {
      id: 'att-4',
      primary: '3 cases ready for review',
      reason: 'Ready for supervisory review',
      severity: 'success',
    },
  ];
}

export function mockGetCurrentUser() {
  return {
    firstName: 'Noah',
    lastName: 'Golding',
    name: 'Noah Golding',
    initials: 'NG',
    role: 'Case Manager',
    organization: 'Veterans Services Office',
  };
}

/* --- Audit log (12 seed entries) --- */
const seedAudit: VcmAuditEntry[] = [
  {
    id: 'aud-1',
    caseId: 'CASE-4879-10482',
    action: 'Case opened',
    performer: 'Noah Golding',
    timestamp: '2026-05-12 09:14',
  },
  {
    id: 'aud-2',
    caseId: 'CASE-4879-10482',
    action: 'Intake completed',
    performer: 'Noah Golding',
    timestamp: '2026-05-12 10:02',
  },
  {
    id: 'aud-3',
    caseId: 'CASE-4879-10482',
    action: 'Release of Information verified',
    performer: 'Noah Golding',
    timestamp: '2026-05-12 10:05',
  },
  {
    id: 'aud-4',
    caseId: 'CASE-4879-10482',
    action: 'Household member added',
    performer: 'Noah Golding',
    timestamp: '2026-05-13 14:21',
  },
  {
    id: 'aud-5',
    caseId: 'CASE-4879-10482',
    action: 'Housing assessment recorded',
    performer: 'Marcus Lee',
    timestamp: '2026-05-15 11:30',
  },
  {
    id: 'aud-6',
    caseId: 'CASE-4879-10482',
    action: 'Service goal created',
    performer: 'Noah Golding',
    timestamp: '2026-05-18 08:45',
  },
  {
    id: 'aud-7',
    caseId: 'CASE-4879-10482',
    action: 'Referral created',
    performer: 'Noah Golding',
    timestamp: '2026-05-20 16:10',
  },
  {
    id: 'aud-8',
    caseId: 'CASE-4879-10482',
    action: 'Tier changed to Tier 3',
    performer: 'Diane Cho',
    timestamp: '2026-05-22 13:05',
  },
  {
    id: 'aud-9',
    caseId: 'CASE-4879-10482',
    action: 'Work order added',
    performer: 'Marcus Lee',
    timestamp: '2026-05-25 09:50',
  },
  {
    id: 'aud-10',
    caseId: 'CASE-4879-10482',
    action: 'Follow-up scheduled',
    performer: 'Noah Golding',
    timestamp: '2026-05-28 15:30',
  },
  {
    id: 'aud-11',
    caseId: 'CASE-4879-10482',
    action: 'Service goal updated',
    performer: 'Noah Golding',
    timestamp: '2026-06-02 10:18',
  },
  {
    id: 'aud-12',
    caseId: 'CASE-4879-10482',
    action: 'Document uploaded',
    performer: 'Noah Golding',
    timestamp: '2026-06-05 12:40',
  },
];

export function mockGetAuditByCaseId(caseId: string): VcmAuditEntry[] {
  const entries = seedAudit.filter((a) => a.caseId === caseId);
  // Fall back to the demo case's trail so every case shows an example timeline.
  return entries.length > 0 ? entries : seedAudit.map((a) => ({ ...a, caseId }));
}

/** Full audit trail across cases (used by the Admin audit viewer). */
export function mockGetAllAudit(): VcmAuditEntry[] {
  const extra: VcmAuditEntry[] = seedCases
    .filter((c) => c.caseId !== 'CASE-4879-10482')
    .flatMap((c, idx) => [
      {
        id: `aud-x-${idx}-1`,
        caseId: c.caseId,
        action: 'Case opened',
        performer: c.assignedManager,
        details: `${c.clientName} · ${c.program}`,
        timestamp: c.createdAt.slice(0, 16).replace('T', ' '),
      },
      {
        id: `aud-x-${idx}-2`,
        caseId: c.caseId,
        action: c.status === 'Closed' ? 'Case closed' : 'Case updated',
        performer: c.assignedManager,
        details: `Status ${c.status} · ${c.tier}`,
        timestamp: c.updatedAt.slice(0, 16).replace('T', ' '),
      },
    ]);
  return [...seedAudit, ...extra];
}

/* --- Work orders (2 samples for Elena Martinez) --- */
const seedWorkOrders: VcmWorkOrder[] = [
  {
    id: 'wo-1',
    caseId: 'CASE-4879-10482',
    damageLevel: 'Needs Repair',
    workOrderType: 'Housing Assessment',
    address: '407 Willow Bend Dr, Georgetown, TX 78626',
    requestor: 'Elena Martinez',
    volOrganization: 'Texas Veterans Commission',
    status: 'In Progress',
  },
  {
    id: 'wo-2',
    caseId: 'CASE-4879-10482',
    damageLevel: 'Stable',
    workOrderType: 'Benefits Counseling',
    address: '407 Willow Bend Dr, Georgetown, TX 78626',
    requestor: 'Elena Martinez',
    volOrganization: 'DAV (Disabled American Veterans)',
    status: 'Completed',
  },
];

export function mockGetWorkOrdersByCaseId(caseId: string): VcmWorkOrder[] {
  return seedWorkOrders.filter((w) => w.caseId === caseId);
}

// ---------------------------------------------------------------------------
// Call Center mock functions
// ---------------------------------------------------------------------------


export function mockGetCallLog(): CallLogEntry[] {
  return [...seedCallLog];
}

export function mockGetCallLogByClientId(clientId: string): CallLogEntry[] {
  return seedCallLog.filter((c) => c.clientId === clientId);
}

export function mockSearchCallers(query: string): VcmClient[] {
  const q = query.toLowerCase().trim();
  if (!q) return [];
  return seedClients.filter((c) =>
    `${c.firstName} ${c.lastName} ${c.phoneHome ?? ''} ${c.email ?? ''} ${c.id} ${c.primaryAddress?.address1 ?? ''} ${c.primaryAddress?.city ?? ''} ${c.county}`.toLowerCase().includes(q),
  );
}
